import { Injectable, computed, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DatePicker } from '@com-delta-omnichannel/mach-core-js';
import {
  FlightStatusForm,
  FlightStatusFormData
} from '../models/interfaces/flight-status-form.interface';
import { FlightStatusFormHandler } from './handlers/flight-status-form.handler';
import { FlightStatusAirportService } from './services/flight-status-airport.service';
import { FlightStatusValidationService } from './services/flight-status-validation.service';
import { FlightStatusService } from './services/flight-status.service';

@Injectable()
export class FlightStatusBaseComponent {
  selectedDateText = '';
  flightStatusForm!: FormGroup;
  content: object = {};
  formSubmitted = false;
  airportService = inject(FlightStatusAirportService);
  formHandler = inject(FlightStatusFormHandler);
  formValidationService = inject(FlightStatusValidationService);
  datePickerService = inject(FlightStatusService);
  private rangeDatePickerInstance: DatePicker | null = null;
  errors: Record<string, string> = {
    departureDate: '',
    flightNumber: '',
    route: '',
    origin: '',
    destination: ''
  };

  protected errorsSignal = signal(this.errors);
  errorCount = computed(() => {
    const errors = this.errorsSignal();
    return Object.values(errors).filter((error) => error && error.trim() !== '')
      .length;
  });

  protected setSelectedDateText(text: string): void {
    this.selectedDateText = text;
  }

  protected clearErrors(): void {
    this.errors = {
      departureDate: '',
      flightNumber: '',
      route: '',
      origin: '',
      destination: ''
    };
    this.errorsSignal.set(this.errors);
  }

  openDatePicker(): void {
    this.rangeDatePickerInstance = this.datePickerService.openDatePicker(
      this.rangeDatePickerInstance,
      (date) => {
        const formattedDate = date.start
          ? this.datePickerService.formatDateForForm(date.start)
          : '';
        this.flightStatusForm.patchValue({ departureDate: formattedDate });
        if (this.formSubmitted) {
          this.formValidationService.validateDateField(
            this.formHandler.getDeparture(this.flightStatusForm),
            this.content as FlightStatusForm,
            this.errors
          );
          this.errorsSignal.set({ ...this.errors });
        }
      },
      (text) => this.setSelectedDateText(text)
    );
  }

  protected updateAirportErrorStates(): void {
    this.airportService.setOriginError(!!this.errors['origin']);
    this.airportService.setDestinationError(!!this.errors['destination']);
  }

  protected emitFormData(): FlightStatusFormData {
    return this.formHandler.createFormData(this.flightStatusForm);
  }

  protected setOriginError(): void {
    const content = this.content as FlightStatusForm;
    this.errors['origin'] =
      content?.errorMessages?.['FROM_CITY_REQD']?.['description'] ||
      'Please enter an Origin';
    this.errorsSignal.set({ ...this.errors });
  }

  protected setDestinationError(): void {
    const content = this.content as FlightStatusForm;
    this.errors['destination'] =
      content?.errorMessages?.['TO_CITY_REQD']?.['description'] ||
      'Please enter a Destination';
    this.errorsSignal.set({ ...this.errors });
  }

  protected validateForm(): void {
    const validationData = this.formValidationService.createValidationData(
      this.flightStatusForm
    );
    this.formValidationService.validateRequiredFields(
      validationData,
      this.content as FlightStatusForm,
      this.errors
    );
    this.formValidationService.validateSameAirport(
      validationData,
      this.content as FlightStatusForm,
      this.errors
    );
    this.errorsSignal.set({ ...this.errors });
    this.updateAirportErrorStates();
  }

  protected validateReactiveRequiredFields(validationData: {
    airportSelection: { origin: string; destination: string };
  }): void {
    const { origin, destination } = validationData.airportSelection;
    if (!origin) this.setOriginError();
    if (!destination) this.setDestinationError();
  }
}
